const LILY_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

class LilyOverlayElement extends HTMLElement {
  connectedCallback() {
    this.returnFocus = null;
    this.openButtons = document.querySelectorAll(`[data-${this.controlName}-open="${this.id}"]`);
    this.closeButtons = this.querySelectorAll(`[data-${this.controlName}-close]`);
    this.openButtons.forEach((button) => button.addEventListener('click', this.open));
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.openButtons?.forEach((button) => button.removeEventListener('click', this.open));
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.close));
    this.removeEventListener('keydown', this.onKeydown);
  }

  open = () => {
    this.returnFocus = document.activeElement;
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lily-scroll-locked');
    requestAnimationFrame(() => this.querySelector(LILY_FOCUSABLE)?.focus());
  };

  close = () => {
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lily-scroll-locked');
    this.returnFocus?.focus();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = [...this.querySelectorAll(LILY_FOCUSABLE)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
}

class LilyDrawer extends LilyOverlayElement {
  controlName = 'drawer';
  transitionDuration = 350;

  connectedCallback() {
    super.connectedCallback();
    this.openFrame = null;
    this.closeTimer = null;
  }

  disconnectedCallback() {
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    window.clearTimeout(this.closeTimer);
    super.disconnectedCallback();
  }

  open = () => {
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    window.clearTimeout(this.closeTimer);
    this.returnFocus = document.activeElement;
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lily-scroll-locked');

    this.openFrame = requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.openFrame = null;
      requestAnimationFrame(() => this.querySelector(LILY_FOCUSABLE)?.focus());
    });
  };

  close = () => {
    if (this.hidden) return;
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    this.openFrame = null;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lily-scroll-locked');
    window.clearTimeout(this.closeTimer);

    this.closeTimer = window.setTimeout(() => {
      this.hidden = true;
      this.closeTimer = null;
      this.returnFocus?.focus();
    }, this.transitionDuration);
  };
}

class LilyModal extends LilyOverlayElement {
  controlName = 'modal';

  open = () => {
    this.returnFocus = document.activeElement;
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lily-scroll-locked');

    const holder = this.querySelector('[data-modal-video]');
    const video = holder?.querySelector('video');
    if (video) {
      if (!video.currentSrc && !video.getAttribute('src')) {
        video.src = holder.dataset.videoSrc;
      }
      video.onerror = () => {
        if (holder.dataset.videoFallbackApplied === 'true') return;
        holder.dataset.videoFallbackApplied = 'true';
        video.src = holder.dataset.videoFallback;
        video.load();
        video.play().catch(() => {});
      };
      video.load();
      video.play().catch(() => {});
    }

    this.dispatchEvent(new CustomEvent('lilyeve:modal-opened', { bubbles: true }));

    requestAnimationFrame(() => this.querySelector(LILY_FOCUSABLE)?.focus());
  };

  close = () => {
    const video = this.querySelector('[data-modal-video] video');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lily-scroll-locked');
    this.dispatchEvent(new CustomEvent('lilyeve:modal-closed', { bubbles: true }));
    this.returnFocus?.focus();
  };
}

customElements.define('lily-drawer', LilyDrawer);
customElements.define('lily-modal', LilyModal);

const siteHeader = document.querySelector('.lily-header');
if (siteHeader) {
  const syncHeaderState = () => siteHeader.classList.toggle('is-scrolled', window.scrollY > 30);
  syncHeaderState();
  window.addEventListener('scroll', syncHeaderState, { passive: true });
}

document.querySelectorAll('[data-lily-localization]').forEach((localization) => {
  const form = localization.querySelector('form');
  const trigger = localization.querySelector('[data-lily-localization-trigger]');
  const menu = localization.querySelector('[data-lily-localization-menu]');
  const hiddenInput = localization.querySelector('[data-lily-localization-input]');
  const options = localization.querySelectorAll('[data-lily-localization-option]');

  if (!form || !trigger || !menu || !hiddenInput) return;

  const close = () => {
    localization.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    menu.hidden = true;
  };

  trigger.addEventListener('click', () => {
    const willOpen = !localization.classList.contains('is-open');
    document.querySelectorAll('[data-lily-localization].is-open').forEach((openSelector) => {
      if (openSelector !== localization) {
        openSelector.querySelector('[data-lily-localization-trigger]')?.setAttribute('aria-expanded', 'false');
        const openMenu = openSelector.querySelector('[data-lily-localization-menu]');
        if (openMenu) openMenu.hidden = true;
        openSelector.classList.remove('is-open');
      }
    });
    localization.classList.toggle('is-open', willOpen);
    trigger.setAttribute('aria-expanded', String(willOpen));
    menu.hidden = !willOpen;
    if (willOpen && window.matchMedia('(min-width: 861px)').matches) {
      menu.querySelector('[aria-selected="true"]')?.focus();
    }
  });

  options.forEach((option) => {
    option.addEventListener('click', () => {
      hiddenInput.value = option.dataset.countryCode;
      form.requestSubmit();
    });
  });

  localization.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    close();
    trigger.focus();
  });

  document.addEventListener('click', (event) => {
    if (!localization.contains(event.target)) close();
  });
});

window.LilyeveModal = {
  open(id) {
    document.getElementById(id)?.open();
  },
  close(id) {
    document.getElementById(id)?.close();
  }
};

document.querySelectorAll('[data-lily-pointer]').forEach((element) => {
  element.addEventListener('pointermove', (event) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bounds = element.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    element.style.setProperty('--pointer-x', x.toFixed(3));
    element.style.setProperty('--pointer-y', y.toFixed(3));
  });

  element.addEventListener('pointerleave', () => {
    element.style.setProperty('--pointer-x', 0);
    element.style.setProperty('--pointer-y', 0);
  });
});

document.querySelectorAll('[data-reviews-more]').forEach((button) => {
  button.addEventListener('click', () => {
    const section = button.closest('[data-lily-reviews]');
    section?.querySelectorAll('[data-review-extra]').forEach((review) => {
      review.hidden = false;
    });
    button.hidden = true;
  });
});

document.querySelectorAll('[data-social-modal-trigger]').forEach((button) => {
  button.addEventListener('click', () => {
    const modal = document.getElementById(button.dataset.modalOpen);
    const image = modal?.querySelector('[data-social-modal-image]');
    if (!image) return;
    image.src = button.dataset.socialSrc;
    image.alt = button.dataset.socialAlt;
    image.width = Number(button.dataset.socialWidth);
    image.height = Number(button.dataset.socialHeight);
  });
});

document.querySelectorAll('a[href="#product"]').forEach((purchaseLink) => {
  purchaseLink.addEventListener('click', (event) => {
    const target = document.querySelector('#product');
    if (!target) return;
    event.preventDefault();
    purchaseLink.closest('lily-drawer')?.close();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
});
